// src/modules/Home/index.js
// VEGA ROBOTICS - Home Page (com i18n, WhatsApp e Modal HubSpot)
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// i18n - Tradução Global
import { useTranslation } from '../../contexts/LanguageContext';

// Supabase
import { supabase } from '../../config/supabase';

// Page Transition
import { usePageTransition } from '../../contexts/PageTransitionContext';

// Icons
import { FaPause, FaPlay, FaStar, FaChevronLeft, FaChevronRight, FaArrowRight, FaEnvelope, FaCalendarAlt, FaPhone, FaWhatsapp } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';

// Modal de Contato HubSpot
import ContactFormModal from '../components/ContactFormModal';

// Assets
import heroVideo from '../../assets/hero-industrial.mp4';
import dep1Photo from '../../assets/dep1.svg';
import dep2Photo from '../../assets/dep2.svg';
import dep3Photo from '../../assets/dep3.svg';
import dep4Photo from '../../assets/dep4.svg';

// Client Logos
import cliente1 from '../../assets/cliente1.svg';
import cliente2 from '../../assets/cliente2.svg';
import cliente3 from '../../assets/cliente3.svg';
import cliente4 from '../../assets/cliente4.svg';
import cliente5 from '../../assets/cliente5.svg';
import cliente6 from '../../assets/cliente6.svg';
import cliente7 from '../../assets/cliente7.svg';
import cliente8 from '../../assets/cliente8.svg';
import cliente9 from '../../assets/cliente9.svg';
import cliente10 from '../../assets/cliente10.svg';
import cliente11 from '../../assets/cliente11.svg';
import cliente12 from '../../assets/cliente12.svg';
import cliente13 from '../../assets/cliente13.svg';
import cliente14 from '../../assets/cliente14.svg';
import cliente15 from '../../assets/cliente15.svg';
import cliente16 from '../../assets/cliente16.svg';
import cliente17 from '../../assets/cliente17.svg';
import cliente18 from '../../assets/cliente18.svg';

// Styles
import {
  PortalContainer,
  Main,
  HeroSection,
  HeroVideo,
  HeroOverlay,
  HeroContent,
  HeroEyebrow,
  HeroTitle,
  StaticText,
  DynamicWrapper,
  DynamicText,
  ExitingText,
  HeroButtonWrapper,
  HeroButton,
  HeroControls,
  ControlButton,
  ClientsSection,
  ClientsContainer,
  ClientsCarouselWrapper,
  ClientsCarouselTrack,
  ClientLogoItem,
  ClientLogoImage,
  ClientLogoPlaceholder,
  SolutionsSection,
  SolutionsContainer,
  SolutionsHeader,
  SolutionsTitle,
  SolutionsGrid,
  SolutionCard,
  SolutionTitle,
  SolutionDescription,
  SolutionLink,
  TestimonialsSection,
  TestimonialsContainer,
  TestimonialsHeader,
  TestimonialsTitle,
  TestimonialsCarouselWrapper,
  TestimonialsCarouselTrack,
  TestimonialsCarouselSlider,
  TestimonialCard,
  TestimonialCardHeader,
  TestimonialAuthorPhoto,
  TestimonialAuthorInfo,
  TestimonialAuthorName,
  TestimonialStarsRow,
  TestimonialText,
  TestimonialsCarouselButton,
  TestimonialsCarouselDots,
  TestimonialsDot,
  CTASection,
  CTAContainer,
  CTATitle,
  CTASubtitle,
  CTAButtons,
  CTAButtonPrimary,
  CTAButtonSecondary,
  CTAContacts,
  CTAContactItem,
  BlogSection,
  BlogContainer,
  BlogHeader,
  BlogTitle,
  BlogSubtitle,
  BlogGrid,
  BlogCard,
  BlogCardImage,
  BlogCardContent,
  BlogCardCategory,
  BlogCardTitle,
  BlogCardExcerpt,
  BlogCardMeta,
  BlogCardMetaDivider,
  BlogButtonWrapper,
  BlogButton,
  WhatsAppFloat,
} from './styles';

// ============================================================
// DATA (estática)
// ============================================================
const VIDEO_DURATION = 16;

const clients = [
  { name: "Cliente 1", logo: cliente1 },
  { name: "Cliente 2", logo: cliente2 },
  { name: "Cliente 3", logo: cliente3 },
  { name: "Cliente 4", logo: cliente4 },
  { name: "Cliente 5", logo: cliente5 },
  { name: "Cliente 6", logo: cliente6 },
  { name: "Cliente 7", logo: cliente7 },
  { name: "Cliente 8", logo: cliente8 },
  { name: "Cliente 9", logo: cliente9 },
  { name: "Cliente 10", logo: cliente10 },
  { name: "Cliente 11", logo: cliente11 },
  { name: "Cliente 12", logo: cliente12 },
  { name: "Cliente 13", logo: cliente13 },
  { name: "Cliente 14", logo: cliente14 },
  { name: "Cliente 15", logo: cliente15 },
  { name: "Cliente 16", logo: cliente16 },
  { name: "Cliente 17", logo: cliente17 },
  { name: "Cliente 18", logo: cliente18 },
];

// Fotos dos depoimentos (fixas)
const testimonialPhotos = [dep4Photo, dep1Photo, dep2Photo, dep3Photo];

// Mapeamento de categorias do Supabase para chaves de tradução
// Os slugs das categorias correspondem diretamente às chaves em blog.categories
const categoryTranslationMap = {
  'tecnologia': 'blog.categories.tecnologia',
  'industria': 'blog.categories.industria',
  'ia': 'blog.categories.ia',
  'inovacao': 'blog.categories.inovacao',
  'cases': 'blog.categories.cases',
  'tutoriais': 'blog.categories.tutoriais',
};

// ============================================================
// HELPERS
// ============================================================
const scrollToSection = (e, targetId) => {
  e.preventDefault();
  const element = document.querySelector(targetId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

// ============================================================
// HERO COMPONENT
// ============================================================
const Hero = ({ videoSrc = heroVideo }) => {
  const { t, tArray } = useTranslation();
  const heroVerbs = tArray('hero.verbs');
  
  const [currentVerb, setCurrentVerb] = useState(0);
  const [exitingVerb, setExitingVerb] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const videoRef = useRef(null);
  const lastVerbRef = useRef(0);
  const lastUpdateRef = useRef(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const now = Date.now();
      if (now - lastUpdateRef.current < 100) return;
      lastUpdateRef.current = now;

      const currentTime = video.currentTime % VIDEO_DURATION;
      const verbIndex = Math.min(Math.floor(currentTime / 2), heroVerbs.length - 1);
      
      if (verbIndex !== lastVerbRef.current) {
        setExitingVerb(lastVerbRef.current);
        lastVerbRef.current = verbIndex;
        setCurrentVerb(verbIndex);
        setTimeout(() => setExitingVerb(null), 1600);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleTimeUpdate);
    video.addEventListener('seeked', handleTimeUpdate);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleTimeUpdate);
      video.removeEventListener('seeked', handleTimeUpdate);
    };
  }, [heroVerbs]);

  const togglePause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPaused) video.play();
    else video.pause();
    setIsPaused(!isPaused);
  };

  return (
    <HeroSection>
      <HeroVideo ref={videoRef} autoPlay muted loop playsInline>
        <source src={videoSrc} type="video/mp4" />
      </HeroVideo>
      <HeroOverlay />
      <HeroContent>
        <HeroEyebrow>{t('hero.eyebrow')}</HeroEyebrow>
        <HeroTitle>
          <StaticText>{t('hero.titleStatic')} </StaticText>
          <DynamicWrapper>
            {exitingVerb !== null && (
              <ExitingText key={`exit-${exitingVerb}`}>{heroVerbs[exitingVerb]}</ExitingText>
            )}
            <DynamicText key={`enter-${currentVerb}`}>{heroVerbs[currentVerb]}</DynamicText>
          </DynamicWrapper>
        </HeroTitle>
      </HeroContent>
      <HeroButtonWrapper>
        <HeroButton href="#solucoes" onClick={(e) => scrollToSection(e, '#solucoes')}>
          {t('hero.button')}
        </HeroButton>
      </HeroButtonWrapper>
    </HeroSection>
  );
};

// ============================================================
// CLIENTS COMPONENT
// ============================================================
const Clients = ({ id = "clientes" }) => {
  const { t } = useTranslation();
  const duplicatedClients = [...clients, ...clients];
  
  return (
    <ClientsSection id={id}>
      <ClientsContainer>
        <ClientsCarouselWrapper role="region" aria-label={t('accessibility.clientsAriaLabel')}>
          <ClientsCarouselTrack aria-hidden="true">
            {duplicatedClients.map((client, index) => (
              <ClientLogoItem key={index}>
                {client.logo ? (
                  <ClientLogoImage src={client.logo} alt={client.name} />
                ) : (
                  <ClientLogoPlaceholder>{client.name}</ClientLogoPlaceholder>
                )}
              </ClientLogoItem>
            ))}
          </ClientsCarouselTrack>
        </ClientsCarouselWrapper>
      </ClientsContainer>
    </ClientsSection>
  );
};

// ============================================================
// SOLUTIONS COMPONENT
// ============================================================
const Solutions = ({ id = "solucoes" }) => {
  const { t } = useTranslation();
  const { startTransition } = usePageTransition();

  const solutions = [
    { titleKey: 'solutions.engenhariaAplicada.name', descriptionKey: 'solutions.engenhariaAplicada.description', link: "https://cadpro.vegarobotics.com.br", external: true, hasTransition: true },
    { titleKey: 'solutions.automacaoIndustrial.name', descriptionKey: 'solutions.automacaoIndustrial.description', link: "/solucoes/automacao", hasTransition: false },
    { titleKey: 'solutions.qualidadeInteligente.name', descriptionKey: 'solutions.qualidadeInteligente.description', link: "/solucoes/vixem", hasTransition: false },
    { titleKey: 'solutions.agentesIndustriais.name', descriptionKey: 'solutions.agentesIndustriais.description', link: "/solucoes/proagentes", hasTransition: false },
  ];

  const handleCADProClick = (e, link) => {
    e.preventDefault();
    startTransition('cadpro', link, true);
  };

  return (
    <SolutionsSection id={id}>
      <SolutionsContainer>
        <SolutionsHeader>
          <SolutionsTitle>{t('solutions.title')}</SolutionsTitle>
        </SolutionsHeader>
        <SolutionsGrid>
          {solutions.map((solution, index) => (
            solution.external ? (
              <SolutionCard
                key={index}
                as="a"
                href={solution.link}
                onClick={solution.hasTransition ? (e) => handleCADProClick(e, solution.link) : undefined}
              >
                <SolutionTitle>{t(solution.titleKey)}</SolutionTitle>
                <SolutionDescription>{t(solution.descriptionKey)}</SolutionDescription>
                <SolutionLink>{t('general.learnMore')} <FaArrowRight /></SolutionLink>
              </SolutionCard>
            ) : (
              <SolutionCard
                key={index}
                as={Link}
                to={solution.link}
              >
                <SolutionTitle>{t(solution.titleKey)}</SolutionTitle>
                <SolutionDescription>{t(solution.descriptionKey)}</SolutionDescription>
                <SolutionLink>{t('general.learnMore')} <FaArrowRight /></SolutionLink>
              </SolutionCard>
            )
          ))}
        </SolutionsGrid>
      </SolutionsContainer>
    </SolutionsSection>
  );
};

// ============================================================
// TESTIMONIALS COMPONENT (com traduções)
// ============================================================
const Testimonials = ({ id = "depoimentos" }) => {
  const { t, tArray } = useTranslation();
  const testimonialItems = tArray('testimonials.items');
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  useEffect(() => {
    const checkBreakpoint = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    checkBreakpoint();
    window.addEventListener('resize', checkBreakpoint);
    return () => window.removeEventListener('resize', checkBreakpoint);
  }, []);
  
  const getMaxIndex = () => {
    if (isMobile) return testimonialItems.length - 1;
    if (isTablet) return testimonialItems.length - 2;
    return testimonialItems.length - 3;
  };
  
  const maxIndex = getMaxIndex();
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [maxIndex]);
  
  const goToPrev = () => setCurrentIndex(prev => Math.max(0, prev - 1));
  const goToNext = () => setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  
  const getOffset = () => {
    if (isMobile) return -currentIndex * 100;
    if (isTablet) return -currentIndex * 50;
    return -currentIndex * 33.33;
  };
  
  return (
    <TestimonialsSection id={id}>
      <TestimonialsContainer>
        <TestimonialsHeader>
          <TestimonialsTitle>{t('testimonials.title')}</TestimonialsTitle>
        </TestimonialsHeader>
        
        <TestimonialsCarouselWrapper>
          <TestimonialsCarouselButton className="prev" onClick={goToPrev} disabled={currentIndex === 0} aria-label={t('testimonials.prev')}>
            <FaChevronLeft />
          </TestimonialsCarouselButton>
          
          <TestimonialsCarouselTrack>
            <TestimonialsCarouselSlider $offset={getOffset()} $mobileOffset={-currentIndex * 100}>
              {testimonialItems.map((testimonial, index) => (
                <TestimonialCard key={index}>
                  <TestimonialCardHeader>
                    <TestimonialAuthorPhoto src={testimonialPhotos[index]} alt={testimonial.author} />
                    <TestimonialAuthorInfo>
                      <TestimonialAuthorName>{testimonial.author}</TestimonialAuthorName>
                      <TestimonialStarsRow>
                        {[...Array(5)].map((_, i) => <FaStar key={i} />)}
                      </TestimonialStarsRow>
                    </TestimonialAuthorInfo>
                  </TestimonialCardHeader>
                  <TestimonialText>{testimonial.text}</TestimonialText>
                </TestimonialCard>
              ))}
            </TestimonialsCarouselSlider>
          </TestimonialsCarouselTrack>
          
          <TestimonialsCarouselButton className="next" onClick={goToNext} disabled={currentIndex >= maxIndex} aria-label={t('testimonials.next')}>
            <FaChevronRight />
          </TestimonialsCarouselButton>
        </TestimonialsCarouselWrapper>
        
        <TestimonialsCarouselDots>
          {[...Array(maxIndex + 1)].map((_, index) => (
            <TestimonialsDot key={index} $active={currentIndex === index} onClick={() => setCurrentIndex(index)} aria-label={`${t('testimonials.goToSlide')} ${index + 1}`} />
          ))}
        </TestimonialsCarouselDots>
      </TestimonialsContainer>
    </TestimonialsSection>
  );
};

// ============================================================
// CTA COMPONENT (com modal)
// ============================================================
const CTA = ({ id = "cta", onOpenModal }) => {
  const { t } = useTranslation();
  const phone = "(11) 94427-1000";
  const email = "contato@vegarobotics.com.br";
  
  return (
    <CTASection id={id}>
      <CTAContainer>
        <CTATitle>{t('cta.title')}</CTATitle>
        <CTASubtitle>{t('cta.subtitle')}</CTASubtitle>
        
        <CTAButtons>
          <CTAButtonPrimary as="button" onClick={onOpenModal}>
            <FaEnvelope />
            {t('general.contactUs')}
          </CTAButtonPrimary>
          <CTAButtonSecondary href="https://calendly.com/vega-vegarobotics/30min" target="_blank" rel="noopener noreferrer">
            <FaCalendarAlt />
            {t('general.scheduleMeeting')}
          </CTAButtonSecondary>
        </CTAButtons>
        
        <CTAContacts>
          <CTAContactItem href={`tel:${phone.replace(/\D/g, '')}`}>
            <FaPhone />
            {phone}
          </CTAContactItem>
          <CTAContactItem href={`mailto:${email}`}>
            <HiOutlineMail />
            {email}
          </CTAContactItem>
        </CTAContacts>
      </CTAContainer>
    </CTASection>
  );
};

// ============================================================
// BLOG COMPONENT (com Supabase)
// ============================================================
const Blog = ({ id = "blog", blogUrl = "https://blog.vegarobotics.com.br" }) => {
  const { t, language } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('posts')
          .select('id, title, slug, excerpt, cover_image, category, created_at, content')
          .eq('status', 'published')
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;
        setPosts(data || []);
      } catch (error) {
        console.error('Erro ao carregar posts:', error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Formatar data baseado no idioma
  const formatDate = (dateString) => {
    const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR';
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace('.', '');
  };

  // Calcular tempo de leitura (palavras / 200 palavras por minuto)
  const calculateReadTime = (content) => {
    if (!content) return 5;
    const words = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  // Obter chave de tradução da categoria
  const getCategoryKey = (category) => {
    return categoryTranslationMap[category] || `blog.categories.${category}`;
  };

  // Se não há posts, não renderiza a seção
  if (!loading && posts.length === 0) {
    return null;
  }

  return (
    <BlogSection id={id}>
      <BlogContainer>
        <BlogHeader>
          <BlogTitle>{t('blog.title')}</BlogTitle>
          <BlogSubtitle>{t('blog.subtitle')}</BlogSubtitle>
        </BlogHeader>

        <BlogGrid>
          {loading ? (
            // Skeleton loading
            [...Array(3)].map((_, index) => (
              <BlogCard key={index} as="div" style={{ cursor: 'default' }}>
                <BlogCardImage style={{ background: '#e0e0e0', animation: 'pulse 1.5s infinite' }} />
                <BlogCardContent>
                  <BlogCardCategory style={{ background: '#e0e0e0', width: '80px', height: '20px' }}>&nbsp;</BlogCardCategory>
                  <BlogCardTitle style={{ background: '#e0e0e0', height: '48px' }}>&nbsp;</BlogCardTitle>
                  <BlogCardExcerpt style={{ background: '#f0f0f0', height: '60px' }}>&nbsp;</BlogCardExcerpt>
                </BlogCardContent>
              </BlogCard>
            ))
          ) : (
            posts.map((post) => (
              <BlogCard key={post.id} as="a" href={`${blogUrl}/${post.slug}`}>
                <BlogCardImage style={post.cover_image ? { backgroundImage: `url(${post.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}} />
                <BlogCardContent>
                  <BlogCardCategory>{t(getCategoryKey(post.category))}</BlogCardCategory>
                  <BlogCardTitle>{post.title}</BlogCardTitle>
                  <BlogCardExcerpt>{post.excerpt}</BlogCardExcerpt>
                  <BlogCardMeta>
                    <span>{formatDate(post.created_at)}</span>
                    <BlogCardMetaDivider />
                    <span>{calculateReadTime(post.content)} {t('blog.readTime')}</span>
                  </BlogCardMeta>
                </BlogCardContent>
              </BlogCard>
            ))
          )}
        </BlogGrid>

        <BlogButtonWrapper>
          <BlogButton as="a" href={blogUrl}>
            {t('blog.viewAll')} <FaArrowRight />
          </BlogButton>
        </BlogButtonWrapper>
      </BlogContainer>
    </BlogSection>
  );
};

// ============================================================
// HOME PAGE
// ============================================================
export const Home = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const whatsappNumber = "+5511944271000";
  const whatsappMessage = encodeURIComponent(t('whatsapp.defaultMessage'));
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
  
  return (
    <PortalContainer>
      <Main>
        <Hero />
        <Clients id="clientes" />
        <Solutions id="solucoes" />
        <Testimonials id="depoimentos" />
        <CTA id="cta" onOpenModal={() => setIsModalOpen(true)} />
        <Blog id="blog" />
      </Main>
      
      {/* WhatsApp Flutuante */}
      <WhatsAppFloat 
        href={whatsappLink} 
        target="_blank" 
        rel="noopener noreferrer" 
        aria-label={t('accessibility.whatsappAriaLabel')}
      >
        <FaWhatsapp />
      </WhatsAppFloat>
      
      {/* Modal de Contato HubSpot */}
      <ContactFormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </PortalContainer>
  );
};

export default Home;
